import type { Bot, BotType, ConversationPersistence } from '@/services/types'
import { isEmbeddedWindow } from '@/utils/windowContext'

export interface AppBotConfig {
  id: string
  name?: string
  type?: BotType
  isStatic?: boolean
  requiresAuth?: boolean
  defaultConversationMode?: ConversationPersistence
  embeddedConversationMode?: ConversationPersistence
  allowAnonymousHistory?: boolean
  allowEmbeddedHistory?: boolean
}

export interface AppConfig {
  apiBaseUrl: string
  bearerToken?: string
  casBaseUrl?: string
  casLoginUrl?: string
  casLogoutUrl?: string
  defaultConversationMode?: ConversationPersistence
  embeddedDefaultConversationMode?: ConversationPersistence
  defaultAnonymousConversationMode?: ConversationPersistence
  staticBotId?: string
  bots?: AppBotConfig[]
}

export interface ConfiguredBot extends Bot {
  isStatic: boolean
  requiresAuth: boolean
  defaultConversationMode?: ConversationPersistence
  embeddedConversationMode?: ConversationPersistence
  allowAnonymousHistory: boolean
  allowEmbeddedHistory: boolean
}

export interface RuntimeContext {
  isEmbedded: boolean
}

let configCache: AppConfig | null = null
const DEFAULT_CONFIG_FILE = 'config.json'

function getConfigFileName(): string {
  const configured = import.meta.env.VITE_APP_CONFIG_NAME
  return typeof configured === 'string' && configured.trim()
    ? configured.trim()
    : DEFAULT_CONFIG_FILE
}

export async function loadConfig(): Promise<AppConfig> {
  if (configCache) return configCache
  
  const response = await fetch(`/${getConfigFileName()}`)
  configCache = await response.json()
  return configCache!
}

// Sync access after config is loaded
export function getConfig(): AppConfig {
  if (!configCache) throw new Error('Config not loaded. Call loadConfig() first.')
  return configCache
}

function toConfiguredBot(config: AppBotConfig): ConfiguredBot {
  return {
    id: config.id,
    name: config.name ?? config.id,
    type: config.type ?? 'departmental',
    isStatic: config.isStatic ?? false,
    requiresAuth: config.requiresAuth ?? false,
    defaultConversationMode: config.defaultConversationMode,
    embeddedConversationMode: config.embeddedConversationMode,
    allowAnonymousHistory: config.allowAnonymousHistory ?? false,
    allowEmbeddedHistory: config.allowEmbeddedHistory ?? false,
  }
}

function isEmbeddedContext(): boolean {
  return isEmbeddedWindow()
}

export function getConfiguredBots(): ConfiguredBot[] {
  const config = getConfig()
  const configuredBots = (config.bots ?? []).map(toConfiguredBot)

  if (config.staticBotId) {
    return configuredBots.map(bot =>
      bot.id === config.staticBotId ? { ...bot, isStatic: true } : bot,
    )
  }

  return configuredBots
}

export function getConfiguredBot(botId: string): ConfiguredBot | undefined {
  return getConfiguredBots().find(bot => bot.id === botId)
}

export function getDefaultBot(): ConfiguredBot | undefined {
  return getConfiguredBots().find(bot => bot.isStatic)
}

export function getRuntimeContext(): RuntimeContext {
  return {
    isEmbedded: isEmbeddedContext(),
  }
}

export function getDefaultConversationMode(
  runtimeContext = getRuntimeContext(),
  authState: { isAuthenticated: boolean },
): ConversationPersistence {
  const config = getConfig()
  if (!authState.isAuthenticated) {
    return config.defaultAnonymousConversationMode ?? 'ephemeral'
  }
  return runtimeContext.isEmbedded
    ? (config.embeddedDefaultConversationMode ?? 'ephemeral')
    : (config.defaultConversationMode ?? 'persistent')
}
